import { defaultEminenceFlagsValues, type EminenceFlags } from "@glide/billing-types";
import type { GetEminenceFlagsBody, GetOwnerEminenceBody } from "@glide/common-core";
import { getEminenceFlagsResponse, getFeatureSetting, getOwnerEminenceResponse } from "@glide/common-core";
import { getAppFacilities } from "@glide/common-core/dist/js/support/app-renderer";
import { getQuadraticBackoffWaitTime, isResponseOK } from "@glide/support";
import type { QueryClient, UseQueryResult } from "@tanstack/react-query";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { isLeft } from "fp-ts/lib/Either";
import type { ComponentType, FunctionComponent } from "react";
import React from "react";
import type { Subtract } from "utility-types";
import { useOrgInfo } from "./use-org-info";

const FIVE_MINUTES_IN_MILLISECONDS = 5 * 60 * 1000;

/**
 * Query key factory for eminence flags queries.
 * Use this to ensure consistent query keys across the application.
 *
 * @param organizationID - The ID of the organization.
 * @returns Query key array for TanStack Query.
 */
function eminenceFlagsQueryKey(organizationID: string | undefined) {
    return ["useEminenceFlags", organizationID];
}

/**
 * Common query options factory for eminence flags queries.
 * Ensures consistent parameters between hook and non-hook usage.
 *
 * @param organizationID - The ID of the organization.
 * @returns Common query options object.
 */
function eminenceFlagsQueryOptions(organizationID: string | undefined) {
    return {
        queryKey: eminenceFlagsQueryKey(organizationID),
        queryFn: () => eminenceFlagsQueryFn(organizationID),
        retryDelay: (attemptIndex: number) => getQuadraticBackoffWaitTime(500, attemptIndex),
        staleTime: FIVE_MINUTES_IN_MILLISECONDS,
    };
}

/**
 * Query function for fetching eminence flags.
 * This can be used both by the React hook and non-hook contexts.
 *
 * @param organizationID - The ID of the organization.
 * @returns Promise that resolves to EminenceFlags.
 */
async function eminenceFlagsQueryFn(organizationID: string | undefined): Promise<EminenceFlags> {
    if (organizationID === undefined) {
        throw new Error("Cannot fetch eminence flags without an organization ID");
    }

    if (getFeatureSetting("fasterBuilderEminenceFlags")) {
        const requestBody: GetEminenceFlagsBody = {
            organizationID,
        };
        const response = await getAppFacilities().callAuthCloudFunction("getEminenceFlags", requestBody);
        if (!isResponseOK(response)) {
            throw new Error("Error fetching getEminenceFlags");
        }

        const decodedResponse = getEminenceFlagsResponse.decode(await response.json());
        if (isLeft(decodedResponse)) {
            throw new Error("Error decoding getEminenceFlags response");
        }

        return decodedResponse.right;
    }

    const body: GetOwnerEminenceBody = {
        ownerID: organizationID,
    };
    const response = await getAppFacilities().callAuthCloudFunction("getOwnerEminence", body);
    if (!isResponseOK(response)) {
        throw new Error("Error fetching getOwnerEminence");
    }

    const decodedResponse = getOwnerEminenceResponse.decode(await response.json());
    if (isLeft(decodedResponse)) {
        throw new Error("Error decoding getOwnerEminence response");
    }

    const organizationEminenceFlags = decodedResponse.right.apps[organizationID];
    if (organizationEminenceFlags === undefined) {
        throw new Error("Organization not found in getOwnerEminence response");
    }
    return organizationEminenceFlags;
}

/**
 * Custom hook to fetch the eminence flags for an organization via TanStack query.
 *
 * @param organizationID - The ID of the organization.
 * @returns A useQuery result for the eminence flags of the organization.
 */
export function useEminenceFlags(organizationID: string | undefined): UseQueryResult<EminenceFlags> {
    return useQuery({
        ...eminenceFlagsQueryOptions(organizationID),
        // Hook-specific options that don't apply to fetchQuery
        refetchOnWindowFocus: false,
        enabled: organizationID !== undefined,
        refetchInterval: FIVE_MINUTES_IN_MILLISECONDS,
    });
}

/**
 * Hook to get the query client for use in callbacks.
 * This allows you to call the non-hook functions above from within React components.
 *
 * @returns The TanStack Query client instance.
 */
export function useEminenceFlagsQueryClient(): QueryClient {
    return useQueryClient();
}

/**
 * Fetches eminence flags using the query client, leveraging cache if available.
 * This function can be called from callbacks or non-React contexts.
 *
 * @param queryClient - The TanStack Query client instance.
 * @param organizationID - The ID of the organization.
 * @returns Promise that resolves to EminenceFlags.
 */
async function fetchEminenceFlags(
    queryClient: QueryClient,
    organizationID: string | undefined
): Promise<EminenceFlags> {
    return await queryClient.fetchQuery(eminenceFlagsQueryOptions(organizationID));
}

/**
 * Hook that provides a callback function to fetch eminence flags for an organization.
 *
 * @returns A callback function that fetches eminence flags when called.
 */
export function useFetchEminenceFlags(): (organizationID: string | undefined) => Promise<EminenceFlags> {
    const queryClient = useEminenceFlagsQueryClient();

    return React.useCallback(
        (organizationID: string | undefined) => fetchEminenceFlags(queryClient, organizationID),
        [queryClient]
    );
}

/**
 * Invalidates the eminence flags cache for a specific organization.
 * Use this after operations that might change eminence flags (like upgrades).
 *
 * @param queryClient - The TanStack Query client instance.
 * @param organizationID - The ID of the organization.
 */
export async function invalidateEminenceFlags(
    queryClient: QueryClient,
    organizationID: string | undefined
): Promise<void> {
    return queryClient.invalidateQueries({
        queryKey: eminenceFlagsQueryKey(organizationID),
    });
}

export interface WithEminenceFlagsProps {
    readonly eminenceFlags: EminenceFlags;
}

/**
 * Higher-order component that provides eminence flags to a component.
 * Uses the new React-Query-based useEminenceFlags hook with organization context.
 *
 * @param Component - The component to wrap with eminence flags
 * @returns A wrapped component that receives eminenceFlags as a prop
 */
export const withEminenceFlags =
    <P extends WithEminenceFlagsProps>(
        Component: ComponentType<P> | React.FC<P>
    ): FunctionComponent<Subtract<P, WithEminenceFlagsProps>> =>
    p => {
        const { orgID } = useOrgInfo();
        const { data: maybeEminenceFlags } = useEminenceFlags(orgID);
        // TODO: consider using TanStack Query's utilities to handle the loading state of the query above. At the moment,
        // the default eminence flags constant is used when the query is loading, which might not be ideal.
        const eminenceFlags = maybeEminenceFlags ?? defaultEminenceFlagsValues;

        return <Component {...(p as P)} eminenceFlags={eminenceFlags} />;
    };

export interface WithFetchEminenceFlagsProps {
    readonly fetchEminenceFlags: () => Promise<EminenceFlags>;
}

/**
 * Higher-order component that an eminence flags getter to a component.
 * Uses the new React-Query-based useEminenceFlags hook with organization context.
 *
 * @param Component - The component to wrap with fetchEminenceFlags function
 * @returns A wrapped component that receives fetchEminenceFlags as a prop
 */
export const withFetchEminenceFlags =
    <P extends WithFetchEminenceFlagsProps>(
        Component: ComponentType<P> | React.FC<P>
    ): FunctionComponent<Subtract<P, WithFetchEminenceFlagsProps>> =>
    p => {
        const fetchEminenceFlagsInner = useFetchEminenceFlags();

        return <Component {...(p as P)} fetchEminenceFlags={fetchEminenceFlagsInner} />;
    };
